import { createContext, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { Alert, AlertItemType } from '../components/custom/popup/Alert';
import { Toast } from '../components/custom/popup/Toast';
import { generateUEID } from '../utils/generator/generateUEID.util';

type ToastItemType = { id: string; content: string };

const ProviderSender = {
  toast: (content: string) => {},
  alert: (props: Omit<AlertItemType, 'id'>) => {},
};

export const ToastContext = createContext<typeof ProviderSender>(ProviderSender);

interface ToastProviderProps {
  children: JSX.Element;
}

export const ToastProvider = ({ children }: ToastProviderProps) => {
  const [toasts, setToasts] = useState<ToastItemType[]>([]);
  const [alerts, setAlerts] = useState<AlertItemType[]>([]);

  const toast = (content: string) => setToasts((currentToasts) => [...currentToasts, { id: generateUEID(), content }]);

  const alert = (props: Omit<AlertItemType, 'id'>) =>
    setAlerts((currentAlerts) => [...currentAlerts, { id: generateUEID(), ...props }]);

  const close = (id: string) => {
    setToasts((currentToasts) => currentToasts.filter((toast) => toast.id !== id));
  };

  const closeAlert = (id: string) => setAlerts((currentAlerts) => currentAlerts.filter((alerts) => alerts.id !== id));

  const contextValue = useMemo(() => ({ toast, alert }), []);

  return (
    <ToastContext.Provider value={contextValue}>
      {children}

      {createPortal(
        <>
          {toasts.map((toast) => (
            <Toast key={toast.id} close={() => close(toast.id)}>
              {toast.content}
            </Toast>
          ))}
          {alerts.map((alert) => (
            <Alert key={alert.id} {...alert} close={() => closeAlert(alert.id)} />
          ))}
        </>,
        document.body,
      )}
    </ToastContext.Provider>
  );
};
